import * as i0 from '@angular/core';
import { Component, ViewChild, Input, Directive, HostBinding, HostListener, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardModule } from '@roosevelt/common-ui-lib/card';
import { InsertableComponent } from '@roosevelt/common-ui-lib/core';

/* eslint-disable max-len */
/**
 * Tooltip Component
 *
 * @description this component allows you to show a tooltip based on a trigger
 * @example
 * <!-- rs-doc {link: 'Passing data as list format to tooltip'} -->
 * <div style="display: flex; justify-content: space-evenly">
 *   <span [rsTooltipTrigger]="tt1">Tooltip</span>
 *   <rs-tooltip color="basic" #tt1>
 *     <ul>
 *       <li>item 1</li>
 *       <li>item 2</li>
 *       <li>item 3</li>
 *     </ul>
 *   </rs-tooltip>
 * <span [rsTooltipTrigger]="tt2">Tooltip</span>
 *   <rs-tooltip #tt2>
 *     <ul>
 *       <li>item 1</li>
 *       <li>item 2</li>
 *       <li>item 3</li>
 *     </ul>
 *   </rs-tooltip>
 * <span [rsTooltipTrigger]="tt3">Tooltip</span>
 *   <rs-tooltip color="secondary" #tt3>
 *     <ul>
 *       <li>item 1</li>
 *       <li>item 2</li>
 *       <li>item 3</li>
 *     </ul>
 *   </rs-tooltip>
 * </div>
 * @example
 * <!-- rs-doc {link: 'Sample of different default positioning'} -->
 * <div style="display: flex; justify-content: space-evenly">
 *   <span [rsTooltipTrigger]="zz1">TOP-BOTTOM LEFT-RIGHT</span>
 *   <rs-tooltip color="basic" #zz1 defaultVerticalAlignment="top-bottom" defaultHorizontalAlignment="left-right">
 *     <ul>
 *       <li>item 1</li>
 *       <li>item 2</li>
 *       <li>item 3</li>
 *     </ul>
 *   </rs-tooltip>
 * <span [rsTooltipTrigger]="zz2">BOTTOM-TOP RIGHT-LEFT</span>
 *   <rs-tooltip #zz2 defaultVerticalAlignment="bottom-top" defaultHorizontalAlignment="right-left">
 *     <ul>
 *       <li>item 1</li>
 *       <li>item 2</li>
 *       <li>item 3</li>
 *     </ul>
 *   </rs-tooltip>
 * <span [rsTooltipTrigger]="zz4">TOP-TOP LEFT-LEFT</span>
 *   <rs-tooltip color="basic" #zz4 defaultVerticalAlignment="top-top" defaultHorizontalAlignment="left-left">
 *     <ul>
 *       <li>item 1</li>
 *       <li>item 2</li>
 *       <li>item 3</li>
 *     </ul>
 *   </rs-tooltip>
 * <span [rsTooltipTrigger]="zz5">BOTTOM-BOTTOM RIGHT-RIGHT</span>
 *   <rs-tooltip #zz5 defaultVerticalAlignment="bottom-bottom" defaultHorizontalAlignment="right-right">
 *     <ul>
 *       <li>item 1</li>
 *       <li>item 2</li>
 *       <li>item 3</li>
 *     </ul>
 *   </rs-tooltip>
 * <span [rsTooltipTrigger]="zz3">DEFAULT</span>
 *   <rs-tooltip color="secondary" #zz3>
 *     <ul>
 *       <li>item 1</li>
 *       <li>item 2</li>
 *       <li>item 3</li>
 *     </ul>
 *   </rs-tooltip>
 * </div>
 * @example
 * <!-- rs-doc {link: 'Extreme Tooltip'} -->
 * <div style="display: flex; justify-content: space-evenly; height: 1500px; align-items:center;">
 *   <span [rsTooltipTrigger]="tt4">Tooltip</span>
 *   <span [rsTooltipTrigger]="tt4">Tooltip</span>
 * </div>
 * <rs-tooltip color="basic" #tt4>
 *     <table style="width: 500px; color: #222" rs-table [dataSource]="[{first: 'a', last: 'b', phone: '111-111-1111'}, {first: 'b', last: 'c', phone: '121-111-1111'}, {first: 'c', last: 'd', phone: '121-111-1111'}, {first: 'd', last: 'e', phone: '121-111-1111'}, {first: 'e', last: 'f', phone: '121-111-1111'}, {first: 'f', last: 'g', phone: '121-111-1111'}, {first: 'g', last: 'h', phone: '121-111-1111'}, {first: 'h', last: 'i', phone: '121-111-1111'}, {first: 'i', last: 'j', phone: '121-111-1111'}, {first: 'j', last: 'k', phone: '121-111-1111'}, {first: 'k', last: 'l', phone: '121-111-1111'}, {first: 'l', last: 'm', phone: '121-111-1111'}, {first: 'm', last: 'n', phone: '121-111-1111'}, {first: 'n', last: 'o', phone: '121-111-1111'}]">
 *       <ng-container rsColumnDef="first">
 *         <th rs-header-cell *rsHeaderCellDef> First Name </th>
 *         <td rs-cell *rsCellDef="let element"> {{element.first}} </td>
 *       </ng-container>
 *       <ng-container rsColumnDef="last">
 *         <th rs-header-cell *rsHeaderCellDef> Last Name </th>
 *         <td rs-cell *rsCellDef="let element"> {{element.last}} </td>
 *       </ng-container>
 *       <ng-container rsColumnDef="phone">
 *         <th rs-header-cell *rsHeaderCellDef> Phone </th>
 *         <td rs-cell *rsCellDef="let element"> {{element.phone}} </td>
 *       </ng-container>
 *         <tr rs-header-row *rsHeaderRowDef="['first', 'last', 'phone']"></tr>
 *         <tr rs-row *rsRowDef="let row; columns: ['first', 'last', 'phone'];"></tr>
 *     </table>
 *   </rs-tooltip>
 */
class TooltipComponent extends InsertableComponent {
    set defaultVerticalAlignment(value) {
        this.insertToTriggerVerticalAlignment = value;
    }
    set defaultHorizontalAlignment(value) {
        this.insertToTriggerHorizontalAlignment = value;
    }
    /** allows the host component to change colors, based on a theme */
    get color() {
        return this._color;
    }
    set color(value) {
        this._getHostElement().classList.add(value);
        if (this._color !== value) {
            this._getHostElement().classList.remove(this._color);
        }
        this._color = value;
    }
    constructor(element, zone) {
        super(zone);
        this.element = element;
        this.elementRef = undefined;
        this._color = 'primary';
    }
    ngOnInit() {
        this.color = this.color;
    }
    ngAfterViewInit() {
        this.linkContent(this.elementRef);
    }
    _getHostElement() {
        return this.element.nativeElement;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TooltipComponent, deps: [{ token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: TooltipComponent, isStandalone: true, selector: "rs-tooltip", inputs: { defaultVerticalAlignment: "defaultVerticalAlignment", defaultHorizontalAlignment: "defaultHorizontalAlignment", color: "color" }, viewQueries: [{ propertyName: "elementRef", first: true, predicate: ["content"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div #content class=\"rs-tooltip\" [style.visibility]=\"visibility\" [style.top.px]=\"top\" [style.left.px]=\"left\">\n  <ng-content></ng-content>\n</div>", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}.rs-tooltip{position:fixed;z-index:99;border-radius:4px;padding:5px}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TooltipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rs-tooltip', standalone: true, template: "<div #content class=\"rs-tooltip\" [style.visibility]=\"visibility\" [style.top.px]=\"top\" [style.left.px]=\"left\">\n  <ng-content></ng-content>\n</div>", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}.rs-tooltip{position:fixed;z-index:99;border-radius:4px;padding:5px}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.NgZone }], propDecorators: { elementRef: [{
                type: ViewChild,
                args: ['content', { static: true }]
            }], defaultVerticalAlignment: [{
                type: Input
            }], defaultHorizontalAlignment: [{
                type: Input
            }], color: [{
                type: Input
            }] } });

class TooltipDirective {
    constructor() {
        /** tooltip component this trigger should link to */
        this.rsTooltipTrigger = undefined;
        this.cursor = 'help';
    }
    mouseOverTrigger(triggerEl) {
        if (this.rsTooltipTrigger.visibility === 'hidden') {
            this.rsTooltipTrigger.insert(triggerEl);
        }
    }
    mouseOutTrigger() {
        this.rsTooltipTrigger.remove();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TooltipDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: TooltipDirective, isStandalone: true, selector: "[rsTooltipTrigger]", inputs: { rsTooltipTrigger: "rsTooltipTrigger" }, host: { listeners: { "mouseover": "mouseOverTrigger($event.target)", "mouseout": "mouseOutTrigger()" }, properties: { "style.cursor": "this.cursor" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rsTooltipTrigger]',
                    standalone: true
                }]
        }], propDecorators: { rsTooltipTrigger: [{
                type: Input
            }], cursor: [{
                type: HostBinding,
                args: ['style.cursor']
            }], mouseOverTrigger: [{
                type: HostListener,
                args: ['mouseover', ['$event.target']]
            }], mouseOutTrigger: [{
                type: HostListener,
                args: ['mouseout', []]
            }] } });

class TooltipModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: TooltipModule, imports: [CommonModule,
            CardModule,
            TooltipComponent, TooltipDirective], exports: [TooltipComponent, TooltipDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TooltipModule, imports: [CommonModule,
            CardModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        CardModule,
                        TooltipComponent, TooltipDirective
                    ],
                    exports: [TooltipComponent, TooltipDirective]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { TooltipComponent, TooltipDirective, TooltipModule };
